/* global JSX */
import { Alert, Box, Chip, Stack } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { ToolTitle } from "../../../framework/Tool/ToolTitle";
import { IMessageEditItemTargetCompanyGroup, MessageEditItemTargetCompanyGroup } from "source/models/message/messageEditItemTargetCompanyGroup";
import { SelectCompanyGroupForPermissionsDialog } from "source/components/companyGroup/SelectCompanyGroupForPermissionsDialog";
import { ICompanyGroupForPermissionListItem } from "source/models/companyGroup/companyGroupForPermissionListItem";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { AddToolButton } from "source/components/framework/Button/AddToolButton";

//CompanyGroupBlockItem
interface ICompanyGroupBlockItemProps {
    group: IMessageEditItemTargetCompanyGroup;
    selectedIds: string[];
    onClick: (id: string) => void;
}

export const CompanyGroupBlockItem = (props: ICompanyGroupBlockItemProps) => {
    const group = props.group;
    const selected = props.selectedIds.indexOf(group.id) > -1;
    return (
        <Chip
            label={group.title}
            avatar={<AvatarImage
                id={group.code}
                marginLeft={"4px"}
                name={group.title}
                image={group.logo}
                tooltip={group.title}
            />}
            variant={selected ? "filled" : "outlined"}
            onClick={(e: { stopPropagation: () => void; }) => { e.stopPropagation(); props.onClick(group.id); }}
        />
    );
};

//MessageEditDialogCompanyGroupBlocks
export interface IIMessageEditDialogCompanyGroupBlocksStateProp {
    companyId: string;
    hasNormalCompanyGroups: boolean;
    hasFixedCompanyGroups: boolean;
    canUseAllCompanyGroup: boolean;
    isReadOnly?: boolean;
    title: string | JSX.Element;
    noUnitsMessage?: string;
    parentIsNew?: boolean;
    parentIsNewMessage?: string;
    margin?: string | number;
    messageTargetCompanyGroups: IMessageEditItemTargetCompanyGroup[];
    onAddMessageTargetCompanyGroups: (messageTargetCompanyGroups: IMessageEditItemTargetCompanyGroup[]) => void;
    onRemoveMessageTargetCompanyGroups: (messageTargetCompanyGroupIds: string[]) => void;
}

interface IMessageEditDialogCompanyGroupBlocksState {
    selectedIds: string[];
    showSelectCompanyGroupDialog: boolean;
}

export class MessageEditDialogCompanyGroupBlocks extends React.Component<IIMessageEditDialogCompanyGroupBlocksStateProp, IMessageEditDialogCompanyGroupBlocksState> {
    constructor(props: IIMessageEditDialogCompanyGroupBlocksStateProp) {
        super(props);
        this.state = {
            selectedIds: [],
            showSelectCompanyGroupDialog: false
        };
    }

    componentDidUpdate(prevProps: IIMessageEditDialogCompanyGroupBlocksStateProp, prevState: IMessageEditDialogCompanyGroupBlocksState) {
        const props = this.props;
        const state = this.state;
        const prevHash = JSON.stringify(prevProps.messageTargetCompanyGroups);
        const hash = JSON.stringify(props.messageTargetCompanyGroups);
        if (prevHash === hash) return;
        const targetIds = props.messageTargetCompanyGroups.map(i => i.id);
        this.setState({
            selectedIds: state.selectedIds.filter(i => targetIds.indexOf(i) > -1)
        });
    }

    handleBlockClick = (id: string) => {
        this.setState({
            selectedIds: [id]
        });
    };

    // #region Add Group
    handleAdd = () => {
        this.setState({
            showSelectCompanyGroupDialog: true
        });
    };

    handleSelectCompanyGroupForPermissionsDialogOk = async (addCompanyGroups: ICompanyGroupForPermissionListItem[]) => {
        const props = this.props;
        if (addCompanyGroups === null || !addCompanyGroups.length) return;
        props.onAddMessageTargetCompanyGroups(addCompanyGroups.map(i => {
            const newGroup = new MessageEditItemTargetCompanyGroup();
            newGroup.id = Base.getGuid();
            newGroup.code = i.code;
            newGroup.title = i.name;
            newGroup.logo = i.logo;
            return newGroup;
        }));
        this.setState({
            showSelectCompanyGroupDialog: false
        });
    };

    handleSelectCompanyGroupForPermissionsDialogCancel = () => {
        this.setState({
            showSelectCompanyGroupDialog: false
        });
    };
    // #endregion Add Group

    // #region Remove Group
    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.selectedIds.length < 1) return;
        props.onRemoveMessageTargetCompanyGroups(state.selectedIds);
    };
    // #endregion Remove Group

    render() {
        const props = this.props;
        const state = this.state;
        const messageTargetCompanyGroups = props.messageTargetCompanyGroups;
        const titleIsString = Base.isString(props.title);
        return (
            <div className="messageEditDialogCompanyGroupBlocks">
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ margin: props.margin ?? "1.5rem 0 0 0" }}>
                    {titleIsString &&
                        <ToolTitle
                            title={props.title}
                        />
                    }
                    {!titleIsString &&
                        props.title
                    }
                    <Stack direction="row">
                        {!props.isReadOnly &&
                            <AddToolButton
                                disabled={props.parentIsNew}
                                tooltip={Translations.AddCompanyGroup}
                                onClick={this.handleAdd}
                            />
                        }
                        {!props.isReadOnly &&
                            <RemoveToolButton
                                disabled={state.selectedIds.length < 1}
                                tooltip={Translations.RemoveCompanyGroup}
                                onClick={this.handleRemove}
                            />
                        }
                    </Stack>
                </Box>
                <Stack direction="row" spacing={1} sx={{ marginTop: ".5rem" }} useFlexGap flexWrap="wrap">
                    {!props.isReadOnly && !!props.parentIsNew && !!props.parentIsNewMessage &&
                        <Alert severity="info" sx={{ width: "100%" }}>{props.parentIsNewMessage}</Alert>
                    }
                    {!props.isReadOnly && messageTargetCompanyGroups.length < 1 && !!props.noUnitsMessage &&
                        <Alert severity="info" sx={{ width: "100%" }}>{props.noUnitsMessage}</Alert>
                    }
                    {messageTargetCompanyGroups.map((messageTargetCompanyGroup) =>
                        <CompanyGroupBlockItem
                            key={messageTargetCompanyGroup.id}
                            group={messageTargetCompanyGroup}
                            selectedIds={state.selectedIds}
                            onClick={this.handleBlockClick}
                        />
                    )}
                </Stack>
                {state.showSelectCompanyGroupDialog &&
                    <SelectCompanyGroupForPermissionsDialog
                        companyId={props.companyId}
                        title={props.hasNormalCompanyGroups ? Translations.SelectCompanyGroupOrCompany : Translations.SelectCompany}
                        ignoreCompanyGroupCodes={props.messageTargetCompanyGroups.map(i => i.code)}
                        multiSelect={true}
                        messageTargetMode={true}
                        canUseAllCompanyGroup={props.canUseAllCompanyGroup}
                        hasNormalCompanyGroups={props.hasNormalCompanyGroups}
                        hasFixedCompanyGroups={props.hasFixedCompanyGroups}
                        onOk={this.handleSelectCompanyGroupForPermissionsDialogOk}
                        onCancel={this.handleSelectCompanyGroupForPermissionsDialogCancel}
                    />
                }
            </div>
        );
    }
}